import type {
  CompanyReviewCompanyReviewRating,
  CompanyReviewLocation,
} from '@seek/ca-graphql-schema/types';

import type { CompanyProfileRatingBreakdown, Reviews } from '@seek/libs-shared';

import type { CompanyDetails } from '../../../shared/services/companyDetailsService/types';
import type { ReviewsTabModel } from './reviewsTabModel';

export const mapCompanyReviewLocationsModel = (
  locations?: CompanyReviewLocation[],
): ReviewsTabModel['locations'] =>
  locations?.length
    ? locations.map((location) => ({
        url: location.relativeUrl,
        name: location.contextualName,
      }))
    : undefined;

export const mapRatingSummaryBreakdown = (
  model: CompanyReviewCompanyReviewRating | null,
): CompanyProfileRatingBreakdown => {
  const companyRating1StarCount = model?.companyRating1Star?.count ?? 0;
  const companyRating2StarCount = model?.companyRating2Star?.count ?? 0;
  const companyRating3StarCount = model?.companyRating3Star?.count ?? 0;
  const companyRating4StarCount = model?.companyRating4Star?.count ?? 0;
  const companyRating5StarCount = model?.companyRating5Star?.count ?? 0;

  const totalRatingCount = model?.company?.count ?? 0;

  const getRatingPercentage = (ratingCount: number): number => {
    if (ratingCount === 0 || totalRatingCount === 0) {
      return 0;
    }
    return (ratingCount / totalRatingCount) * 100;
  };

  return {
    companyRating1StarCount,
    companyRating1StarPercentage: getRatingPercentage(companyRating1StarCount),
    companyRating2StarCount,
    companyRating2StarPercentage: getRatingPercentage(companyRating2StarCount),
    companyRating3StarCount,
    companyRating3StarPercentage: getRatingPercentage(companyRating3StarCount),
    companyRating4StarCount,
    companyRating4StarPercentage: getRatingPercentage(companyRating4StarCount),
    companyRating5StarCount,
    companyRating5StarPercentage: getRatingPercentage(companyRating5StarCount),
  };
};

export const mapReviewModel = (
  companyDetails: CompanyDetails,
  reviews: Reviews,
  companyReviewLocations?: CompanyReviewLocation[],
): ReviewsTabModel => ({
  reviewSummary: {
    name: companyDetails.companyProfile?.data?.name ?? '',
    ratingBreakdown: mapRatingSummaryBreakdown(
      companyDetails?.companyProfile?.companyReviewRating,
    ),
    salaryRating:
      (companyDetails.companyProfile.companyReviewRating?.salaryFair
        ?.percentage ?? 0) +
        (companyDetails.companyProfile.companyReviewRating?.salaryGenerous
          ?.percentage ?? 0) || undefined,
    employeeRecommendationRating:
      companyDetails.companyProfile.companyReviewRating?.companyRecommended
        ?.percentage ?? undefined,
    companyReviewSummaryBreakdown: [
      ...(companyDetails.companyProfile.companyReviewRating?.workLifeBalance
        ?.count
        ? [
            {
              rating:
                companyDetails.companyProfile.companyReviewRating
                  ?.workLifeBalance?.rating ?? 0,
              count:
                companyDetails.companyProfile.companyReviewRating
                  ?.workLifeBalance?.count ?? 0,
              description: 'Work/Life balance',
            },
          ]
        : []),
      ...(companyDetails.companyProfile.companyReviewRating?.careerOpportunity
        ?.count
        ? [
            {
              rating:
                companyDetails.companyProfile.companyReviewRating
                  ?.careerOpportunity?.rating ?? 0,
              count:
                companyDetails.companyProfile.companyReviewRating
                  .careerOpportunity.count,
              description: 'Career development',
            },
          ]
        : []),
      ...(companyDetails.companyProfile.companyReviewRating?.perksAndBenefits
        ?.count
        ? [
            {
              rating:
                companyDetails.companyProfile.companyReviewRating
                  ?.perksAndBenefits?.rating ?? 0,
              count:
                companyDetails.companyProfile.companyReviewRating
                  ?.perksAndBenefits?.count ?? 0,
              description: 'Benefits & perks',
            },
          ]
        : []),
      ...(companyDetails.companyProfile.companyReviewRating?.executiveManagement
        ?.count
        ? [
            {
              rating:
                companyDetails.companyProfile.companyReviewRating
                  ?.executiveManagement?.rating ?? 0,
              count:
                companyDetails.companyProfile.companyReviewRating
                  ?.executiveManagement?.count ?? 0,
              description: 'Management',
            },
          ]
        : []),
      ...(companyDetails.companyProfile.companyReviewRating?.workEnvironment
        ?.count
        ? [
            {
              rating:
                companyDetails.companyProfile.companyReviewRating
                  ?.workEnvironment?.rating ?? 0,
              count:
                companyDetails.companyProfile.companyReviewRating
                  ?.workEnvironment?.count ?? 0,
              description: 'Working environment',
            },
          ]
        : []),
      ...(companyDetails.companyProfile.companyReviewRating?.diversity?.count
        ? [
            {
              rating:
                companyDetails.companyProfile.companyReviewRating?.diversity
                  ?.rating ?? 0,
              count:
                companyDetails.companyProfile.companyReviewRating?.diversity
                  ?.count ?? 0,
              description: 'Diversity & equal opportunity',
            },
          ]
        : []),
    ],
    overallRating: companyDetails.companyProfile.companyReviewRating?.company
      ?.count
      ? {
          average: companyDetails.companyProfile.companyReviewRating?.company
            ?.rating as number,
          count:
            companyDetails.companyProfile.companyReviewRating?.company?.count,
        }
      : undefined,
  },
  reviews: {
    ...reviews,
    companyId: companyDetails.id as string,
  },
  name: companyDetails.companyProfile?.data?.name ?? '',
  overview: {
    industry:
      companyDetails.companyProfile.data.overview?.industry ?? undefined,
    primaryLocation:
      companyDetails.companyProfile.data.overview?.primaryLocation ?? undefined,
    website: companyDetails.companyProfile.data.overview?.website
      ? {
          url: companyDetails.companyProfile.data.overview?.website.url,
          displayName: companyDetails.companyProfile.data.overview?.website
            .displayName as string,
        }
      : undefined,
    description:
      (companyDetails.companyProfile.data.overview?.description as string[]) ??
      '',
    specialities:
      (companyDetails.companyProfile.data.overview?.specialities as string[]) ??
      undefined,
    companySize: companyDetails.companyProfile.data.overview?.size ?? undefined,
  },
  branding: companyDetails.companyProfile.data.branding
    ? {
        brandId:
          companyDetails.companyProfile.data.branding.brandId ?? undefined,
        logoImageUrl:
          companyDetails.companyProfile.data.branding.logo ?? undefined,
        coverImageUrl:
          companyDetails.companyProfile.data.branding.cover ?? undefined,
      }
    : undefined,
  locations: mapCompanyReviewLocationsModel(companyReviewLocations),
  shouldIndex: Boolean(reviews?.reviews?.length),
});
